import React, { useContext, useMemo, useState } from 'react';

import { useLocalStorageState } from '@/utils/use-local-storage-state';

type ExpandCollapseContextType = {
  expansionStates: Map<string, boolean>;
  globalExpandState: boolean | null;
  expandAll: () => void;
  collapseAll: () => void;
  updateChildState: (id: string, isExpanded: boolean) => void;
  unregister: (id: string) => void;
  areAllExpanded: () => boolean;
};

const ExpandCollapseContext =
  React.createContext<ExpandCollapseContextType | null>(null);

export const ExpandCollapseProvider = ({
  children,
  localStorageKey,
}: {
  children: React.ReactNode;
  localStorageKey: `ls:${string}`;
}) => {
  const [expansionStates, setExpansionStates] = useState(
    new Map<string, boolean>()
  );
  const [globalExpandState, setGlobalExpandState] = useLocalStorageState<
    boolean | null
  >(localStorageKey, null);

  const contextValue = useMemo(
    () => ({
      expansionStates,
      globalExpandState,
      updateChildState: (id: string, isExpanded: boolean) => {
        setExpansionStates((prev) => {
          const next = new Map(prev);
          next.set(id, isExpanded);
          return next;
        });
      },
      unregister: (id: string) => {
        setExpansionStates((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      },
      expandAll: () => {
        setExpansionStates((prev) => {
          const next = new Map(prev);
          Array.from(next.keys()).forEach((k) => next.set(k, true));
          return next;
        });
        setGlobalExpandState(true);
      },
      collapseAll: () => {
        setExpansionStates((prev) => {
          const next = new Map(prev);
          Array.from(next.keys()).forEach((k) => next.set(k, false));
          return next;
        });
        setGlobalExpandState(false);
      },
      areAllExpanded: () =>
        Array.from(expansionStates.values()).every((v) => v),
    }),
    [expansionStates, globalExpandState, setGlobalExpandState]
  );

  return (
    <ExpandCollapseContext.Provider value={contextValue}>
      {children}
    </ExpandCollapseContext.Provider>
  );
};

export const useExpandCollapse = () => {
  const context = useContext(ExpandCollapseContext);
  if (!context) return null;
  return context;
};
